import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import {
	Calendar,
	ChevronLeft,
	ChevronRight,
	Edit2,
	Loader2,
	MapPin,
	Plus,
	Save,
	Trash2,
} from "lucide-react";
import { fetchAPI, formatDate } from "../../lib/utils";
import { useLanguage } from "../../contexts/LanguageContext";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { useReadableColor } from "../../lib/useChartColors";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
} from "../ui/dialog";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "../ui/tooltip";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/select";
import {
	CompetitionEventDialog,
	EMPTY_COMPETITION_FORM,
	competitionFormFromEvent,
} from "./CompetitionEventDialog";
import {
	CompetitionParticipantsDialog,
	SharedAthletesBadge,
} from "./CompetitionParticipantsDialog";

/**
 * Season planning for one athlete: mesocycle phases as coloured date ranges
 * and competitions as day chips on a monthly calendar, with drag-to-create for
 * phases and the two create/edit dialogs.
 *
 * Extracted verbatim from pages/AthleteDetail.js so the same calendar can live
 * in the ProgramBuilder dock (docs/program-builder-season-strip, Fase 1b).
 * Data is controlled: the parent owns `mesocycles` / `competitions` and is
 * told to reload through the two callbacks after every write.
 */

// Predefined mesocycle colors
const MESOCYCLE_COLORS = [
	"#007AFF", // volt blue
	"#FF3B30", // blaze red
	"#CCFF00", // acid green
	"#FF9500", // orange
	"#AF52DE", // purple
	"#5AC8FA", // cyan
	"#FF2D55", // pink
	"#30D158", // green
	"#FFD60A", // yellow
	"#BF5AF2", // violet
];

export function AthletePlanningCalendar({
	athleteId,
	mesocycles,
	competitions,
	loading = false,
	onMesocyclesChanged,
	onCompetitionsChanged,
	// Dock mode (ProgramBuilder): one fixed month, the week open in the editor
	// highlighted, denser grids inside a 340-720px column.
	monthsSelectable = true,
	defaultMonthsToShow,
	focusMonth,
	highlightRange,
	compact = false,
	// Group scope (Fase 2): phases are written to the group and every member
	// inherits them; a race is created for every member in one bulk call.
	groupId,
	memberAthleteIds,
}) {
	const readable = useReadableColor();
	const { t, language } = useLanguage();

	// Calendar state
	const [calendarMonth, setCalendarMonth] = useState(() => {
		if (focusMonth) return focusMonth;
		const now = new Date();
		return { year: now.getFullYear(), month: now.getMonth() };
	});
	// The parent can steer the visible month (the builder follows the week
	// open in the editor); manual prev/next still work until the next change.
	const focusYear = focusMonth?.year;
	const focusMonthIndex = focusMonth?.month;
	useEffect(() => {
		if (focusYear === undefined || focusMonthIndex === undefined) return;
		setCalendarMonth({ year: focusYear, month: focusMonthIndex });
	}, [focusYear, focusMonthIndex]);
	const [monthsToShow, setMonthsToShow] = useState(() => {
		if (defaultMonthsToShow) return defaultMonthsToShow;
		if (typeof window === "undefined") return 3;
		return window.innerWidth < 768 ? 1 : 3;
	});
	const [isCompetitionDialogOpen, setIsCompetitionDialogOpen] = useState(false);
	const [editingCompetition, setEditingCompetition] = useState(null);
	const [competitionForm, setCompetitionForm] = useState(
		EMPTY_COMPETITION_FORM,
	);

	const [participantsEvent, setParticipantsEvent] = useState(null);
	const [participants, setParticipants] = useState([]);
	const [participantsLoading, setParticipantsLoading] = useState(false);

	const [isMesocycleDialogOpen, setIsMesocycleDialogOpen] = useState(false);
	const [editingMesocycle, setEditingMesocycle] = useState(null);
	const [mesocycleForm, setMesocycleForm] = useState({
		name: "",
		description: "",
		color: MESOCYCLE_COLORS[0],
		start_date: "",
		end_date: "",
	});
	const [mesocycleSelection, setMesocycleSelection] = useState({
		anchor: null,
		current: null,
		active: false,
	});

	// ── Mesocycle CRUD ──
	const getOrderedMesocycleRange = (startDate, endDate) => {
		if (!startDate || !endDate) return null;
		return startDate <= endDate
			? { start_date: startDate, end_date: endDate }
			: { start_date: endDate, end_date: startDate };
	};

	const clearMesocycleSelection = useCallback(() => {
		setMesocycleSelection({ anchor: null, current: null, active: false });
	}, []);

	const openNewMesocycle = useCallback(
		(range = {}) => {
			setEditingMesocycle(null);
			clearMesocycleSelection();
			setMesocycleForm({
				name: "",
				description: "",
				color: MESOCYCLE_COLORS[0],
				start_date: range.start_date || "",
				end_date: range.end_date || range.start_date || "",
			});
			setIsMesocycleDialogOpen(true);
		},
		[clearMesocycleSelection],
	);

	const openEditMesocycle = (meso) => {
		clearMesocycleSelection();
		setEditingMesocycle(meso);
		setMesocycleForm({
			name: meso.name,
			description: meso.description || "",
			color: meso.color,
			start_date: meso.start_date,
			end_date: meso.end_date,
		});
		setIsMesocycleDialogOpen(true);
	};

	const startMesocycleSelection = (dateStr) => {
		if (!dateStr) return;
		setMesocycleSelection({
			anchor: dateStr,
			current: dateStr,
			active: true,
		});
	};

	const updateMesocycleSelection = (dateStr) => {
		if (!dateStr) return;
		setMesocycleSelection((prev) =>
			prev.active ? { ...prev, current: dateStr } : prev,
		);
	};

	const saveMesocycle = async () => {
		if (
			!mesocycleForm.name ||
			!mesocycleForm.start_date ||
			!mesocycleForm.end_date
		) {
			toast.error(t("fillRequiredFields"));
			return;
		}
		if (mesocycleForm.start_date > mesocycleForm.end_date) {
			toast.error(t("mesocycleInvalidDates"));
			return;
		}
		try {
			if (editingMesocycle) {
				await fetchAPI(`/mesocycles/${editingMesocycle.mesocycle_id}`, {
					method: "PUT",
					body: JSON.stringify(mesocycleForm),
				});
				toast.success(t("mesocycleUpdated"));
			} else {
				await fetchAPI(
					groupId
						? `/mesocycles/group/${groupId}`
						: `/mesocycles/athlete/${athleteId}`,
					{
						method: "POST",
						body: JSON.stringify(mesocycleForm),
					},
				);
				toast.success(t("mesocycleCreated"));
			}
			setIsMesocycleDialogOpen(false);
			onMesocyclesChanged?.();
		} catch {
			toast.error(t("error"));
		}
	};

	const deleteMesocycle = async (mesoId) => {
		try {
			await fetchAPI(`/mesocycles/${mesoId}`, { method: "DELETE" });
			toast.success(t("mesocycleDeleted"));
			onMesocyclesChanged?.();
		} catch {
			toast.error(t("error"));
		}
	};

	const openCompetitionDialog = (selectedDate = "") => {
		setEditingCompetition(null);
		setCompetitionForm({ ...EMPTY_COMPETITION_FORM, date: selectedDate });
		setIsCompetitionDialogOpen(true);
	};

	// Who else is on this race: same roster dialog as the competition hub.
	const openCompetitionParticipants = async (event) => {
		setParticipantsEvent(event);
		setParticipants([]);
		setParticipantsLoading(true);
		try {
			const data = await fetchAPI(
				`/coach/competitions/${event.event_id}/participants`,
			);
			setParticipants(data?.athletes || []);
		} catch {
			toast.error(t("competitionParticipantsError"));
		} finally {
			setParticipantsLoading(false);
		}
	};

	const openEditCompetition = (event) => {
		setEditingCompetition(event);
		setCompetitionForm(competitionFormFromEvent(event));
		setIsCompetitionDialogOpen(true);
	};

	// A race is stored as one row per athlete. In group scope the calendar shows
	// a single representative row, so edits and deletions have to reach every
	// member's row or the group would silently drift apart.
	const competitionGroupEventIds = async (event) => {
		const participants = await fetchAPI(
			`/coach/competitions/${event.event_id}/participants`,
		);
		const ids = participants?.event_ids || [];
		return ids.length > 0 ? ids : [event.event_id];
	};

	const deleteCompetitionEvent = async (event) => {
		if (!window.confirm(t("competitionHubDeleteConfirm"))) return;
		try {
			if (groupId) {
				await fetchAPI("/coach/competitions/bulk-action", {
					method: "POST",
					body: JSON.stringify({
						event_ids: await competitionGroupEventIds(event),
						action: "delete",
					}),
				});
			} else {
				await fetchAPI(`/coach/competitions/${event.event_id}`, {
					method: "DELETE",
				});
			}
			toast.success(t("eventDeleted"));
			onCompetitionsChanged?.();
		} catch {
			toast.error(t("error"));
		}
	};

	const saveCompetitionEvent = async () => {
		if (!competitionForm.name || !competitionForm.date) {
			toast.error(t("fillRequiredFields"));
			return;
		}

		try {
			if (editingCompetition) {
				// In group scope the race fields are propagated to the other
				// athletes attending it; personal fields stay on their own row.
				const query = groupId ? "?apply_to_group=true" : "";
				await fetchAPI(
					`/coach/competitions/${editingCompetition.event_id}${query}`,
					{
						method: "PUT",
						body: JSON.stringify(competitionForm),
					},
				);
				toast.success(t("eventUpdated"));
				setIsCompetitionDialogOpen(false);
				setEditingCompetition(null);
				onCompetitionsChanged?.();
				return;
			}
			if (groupId) {
				// One row per athlete is how races are stored; the bulk endpoint
				// fans the same race out to every member of the group.
				await fetchAPI("/coach/competitions/bulk", {
					method: "POST",
					body: JSON.stringify({
						...competitionForm,
						athlete_ids: memberAthleteIds || [],
					}),
				});
			} else {
				await fetchAPI(`/coach/athletes/${athleteId}/competitions`, {
					method: "POST",
					body: JSON.stringify(competitionForm),
				});
			}
			toast.success(t("eventAdded"));
			setIsCompetitionDialogOpen(false);
			onCompetitionsChanged?.();
		} catch {
			toast.error(t("error"));
		}
	};

	// ── Calendar helpers ──
	const getDaysInMonth = (year, month) =>
		new Date(year, month + 1, 0).getDate();
	const getFirstDayOfWeek = (year, month) => {
		const day = new Date(year, month, 1).getDay();
		return day === 0 ? 6 : day - 1; // Monday = 0
	};

	const prevMonth = () => {
		setCalendarMonth((prev) => {
			if (prev.month === 0) return { year: prev.year - 1, month: 11 };
			return { year: prev.year, month: prev.month - 1 };
		});
	};
	const nextMonth = () => {
		setCalendarMonth((prev) => {
			if (prev.month === 11) return { year: prev.year + 1, month: 0 };
			return { year: prev.year, month: prev.month + 1 };
		});
	};

	const monthRangeLabel = (() => {
		const first = new Date(calendarMonth.year, calendarMonth.month, 1);
		const last = new Date(
			calendarMonth.year,
			calendarMonth.month + monthsToShow - 1,
			1,
		);
		const locale = language === "it" ? "it-IT" : "en-US";
		const firstLabel = first.toLocaleDateString(locale, {
			month: "long",
			year: "numeric",
		});
		const lastLabel = last.toLocaleDateString(locale, {
			month: "long",
			year: "numeric",
		});
		return firstLabel === lastLabel
			? firstLabel
			: `${firstLabel} - ${lastLabel}`;
	})();

	const getCompetitionsForDate = (dateStr) => {
		return competitions.filter((e) => e.date === dateStr);
	};

	const getMesocyclesForDate = (dateStr) => {
		return mesocycles.filter(
			(m) => m.start_date <= dateStr && m.end_date >= dateStr,
		);
	};

	const selectedMesocycleRange = getOrderedMesocycleRange(
		mesocycleSelection.anchor,
		mesocycleSelection.current,
	);

	const dayNames =
		language === "it"
			? ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"]
			: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

	useEffect(() => {
		if (!mesocycleSelection.active) return undefined;
		const handleMouseUp = () => {
			const orderedRange = getOrderedMesocycleRange(
				mesocycleSelection.anchor,
				mesocycleSelection.current,
			);
			clearMesocycleSelection();
			if (!orderedRange) return;
			openNewMesocycle(orderedRange);
		};
		window.addEventListener("mouseup", handleMouseUp);
		return () => window.removeEventListener("mouseup", handleMouseUp);
	}, [mesocycleSelection, openNewMesocycle, clearMesocycleSelection]);

	// ── Calendar Grid ──
	const renderCalendar = (year, month) => {
		const daysInMonth = getDaysInMonth(year, month);
		const firstDay = getFirstDayOfWeek(year, month);
		const cells = [];

		// Empty leading cells
		for (let i = 0; i < firstDay; i++) {
			cells.push(
				<div key={`empty-${i}`} className="h-20 border border-border/30" />,
			);
		}

		for (let day = 1; day <= daysInMonth; day++) {
			const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
			const dayComps = getCompetitionsForDate(dateStr);
			const dayMesos = getMesocyclesForDate(dateStr);
			const isToday = dateStr === new Date().toISOString().split("T")[0];
			const isSelectedForCreation =
				selectedMesocycleRange &&
				dateStr >= selectedMesocycleRange.start_date &&
				dateStr <= selectedMesocycleRange.end_date;
			const isHighlighted =
				Boolean(highlightRange) &&
				dateStr >= highlightRange.start &&
				dateStr <= highlightRange.end;

			// Determine background from first mesocycle
			const mesoColor = dayMesos.length > 0 ? dayMesos[0].color : null;

			cells.push(
				<TooltipProvider key={day} delayDuration={200}>
					<Tooltip>
						<TooltipTrigger asChild>
							<div
								onMouseDown={() => startMesocycleSelection(dateStr)}
								onMouseEnter={() => updateMesocycleSelection(dateStr)}
								className={`h-20 border border-border/30 p-1 relative cursor-crosshair select-none transition-colors ${
									isToday ? "ring-1 ring-volt" : ""
								} ${isSelectedForCreation ? "bg-volt/15 border-volt/60" : ""} ${
									isHighlighted && !isSelectedForCreation
										? "ring-1 ring-inset ring-acid/60"
										: ""
								}`}
								style={
									!isSelectedForCreation && mesoColor
										? {
												backgroundColor: `${mesoColor}15`,
												borderColor: `${mesoColor}40`,
											}
										: undefined
								}
							>
								<span
									className={`text-xs font-medium ${
										isToday ? "text-volt" : "text-muted-foreground"
									}`}
								>
									{day}
								</span>
								{dayComps.length > 0 && (
									<div className="absolute bottom-1 left-1 right-1">
										{dayComps.map((comp) => (
											<button
												type="button"
												key={comp.event_id}
												// The cell itself starts a phase drag: without this the
												// click on the race would open the new-phase dialog.
												onMouseDown={(clickEvent) =>
													clickEvent.stopPropagation()
												}
												onClick={(clickEvent) => {
													clickEvent.stopPropagation();
													openEditCompetition(comp);
												}}
												title={comp.name}
												className="block w-full text-left text-[11px] leading-tight bg-blaze/20 text-blaze px-1 rounded-sm truncate mb-0.5 cursor-pointer hover:bg-blaze/35"
											>
												{comp.name}
											</button>
										))}
									</div>
								)}
								{dayMesos.length > 0 && dayComps.length === 0 && (
									<div className="absolute bottom-1 left-1">
										{dayMesos.map((m) => (
											<div
												key={m.mesocycle_id}
												className="w-2 h-2 rounded-full inline-block mr-0.5"
												style={{ backgroundColor: m.color }}
											/>
										))}
									</div>
								)}
								{isSelectedForCreation && (
									<div className="absolute inset-x-1 bottom-1 text-[11px] font-medium text-volt truncate">
										{t("selectedRangeLabel")}
									</div>
								)}
							</div>
						</TooltipTrigger>
						<TooltipContent
							side="top"
							className="rounded-sm max-w-xs"
						>
							<div className="space-y-1">
								<p className="text-xs font-medium">
									{new Date(dateStr + "T12:00:00").toLocaleDateString(
										language === "it" ? "it-IT" : "en-US",
										{ day: "numeric", month: "long", year: "numeric" },
									)}
								</p>
								{dayComps.map((comp) => (
									<div key={comp.event_id} className="text-xs">
										<span className="text-blaze font-medium">{comp.name}</span>
										{comp.event_type && (
											<span className="text-muted-foreground ml-1">
												({comp.event_type})
											</span>
										)}
										{comp.location && (
											<span className="text-muted-foreground flex items-center gap-1 mt-0.5">
												<MapPin className="w-3 h-3" />
												{comp.location}
											</span>
										)}
										{comp.notes && (
											<p className="text-muted-foreground text-[10px] mt-0.5">
												{comp.notes}
											</p>
										)}
									</div>
								))}
								{dayMesos.map((m) => (
									<div key={m.mesocycle_id} className="text-xs">
										<span
											className="font-medium"
											style={{ color: readable(m.color) }}
										>
											{m.name}
										</span>
										{m.description && (
											<p className="text-muted-foreground text-[10px]">
												{m.description}
											</p>
										)}
									</div>
								))}
								{dayComps.length === 0 && dayMesos.length === 0 && (
									<p className="text-xs text-muted-foreground">
										{t("noEventsThisDay")}
									</p>
								)}
							</div>
						</TooltipContent>
					</Tooltip>
				</TooltipProvider>,
			);
		}

		return cells;
	};

	const visibleMonths = Array.from({ length: monthsToShow }, (_, index) => {
		const date = new Date(calendarMonth.year, calendarMonth.month + index, 1);
		return {
			year: date.getFullYear(),
			month: date.getMonth(),
			label: date.toLocaleDateString(language === "it" ? "it-IT" : "en-US", {
				month: "long",
				year: "numeric",
			}),
		};
	});

	return (
		<>
			<div className="space-y-6">
				{/* Mesocycle Legend + Add */}
				<div className="space-y-3">
					<div className="space-y-1">
						<p className="label-tactical">{t("mesocycles")}</p>
						<p className="text-xs text-muted-foreground max-w-2xl">
							{t("mesocycleDragHint")}
						</p>
					</div>
					<div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
						<Button
							variant="outline"
							onClick={() => openCompetitionDialog()}
							className="w-full border-dashed border-volt/50 text-volt hover:bg-volt/10 rounded-sm h-9 text-xs justify-center"
						>
							<Calendar className="w-3.5 h-3.5 mr-1" />
							{t("coachAddCompetitionEvent")}
						</Button>
						<Button
							onClick={openNewMesocycle}
							className="w-full bg-volt hover:bg-volt/90 rounded-sm h-9 text-xs justify-center"
						>
							<Plus className="w-3.5 h-3.5 mr-1" />
							{t("addMesocycle")}
						</Button>
					</div>
				</div>

				{/* Mesocycle cards */}
				{mesocycles.length > 0 && (
					<div
						className={`grid gap-2 ${
							compact ? "grid-cols-1" : "sm:grid-cols-2 lg:grid-cols-3"
						}`}
					>
						{mesocycles.map((meso) => {
							// A phase inherited from a group is edited from the group's
							// own planning, not from a single member's calendar.
							const isInheritedGroupPhase =
								!groupId && meso.source === "group";
							return (
							<Card
								key={meso.mesocycle_id}
								className={`bg-card border-border rounded-sm transition-colors ${
									isInheritedGroupPhase
										? ""
										: "cursor-pointer hover:border-volt/50"
								}`}
								onClick={() =>
									!isInheritedGroupPhase && openEditMesocycle(meso)
								}
								style={{ borderLeftWidth: 3, borderLeftColor: meso.color }}
								title={isInheritedGroupPhase ? t("mesocycleGroupReadonly") : undefined}
							>
								<CardContent className="p-3 space-y-1">
									<div className="flex items-start justify-between gap-2">
										<div className="flex items-center gap-1.5 min-w-0">
											<p className="font-heading text-sm truncate">
												{meso.name}
											</p>
											{isInheritedGroupPhase && (
												<Badge className="bg-secondary text-muted-foreground border-0 text-[10px] shrink-0">
													{t("pbSeasonGroupBadge")}
													{meso.group_name ? ` · ${meso.group_name}` : ""}
												</Badge>
											)}
										</div>
										{!isInheritedGroupPhase && (
										<div className="flex gap-1 shrink-0">
											<button
												onClick={(event) => {
													event.stopPropagation();
													openEditMesocycle(meso);
												}}
												className="text-muted-foreground hover:text-volt p-0.5"
											>
												<Edit2 className="w-3.5 h-3.5" />
											</button>
											<button
												onClick={(event) => {
													event.stopPropagation();
													deleteMesocycle(meso.mesocycle_id);
												}}
												className="text-muted-foreground hover:text-blaze p-0.5"
											>
												<Trash2 className="w-3.5 h-3.5" />
											</button>
										</div>
										)}
									</div>
									{meso.description && (
										<p className="text-xs text-muted-foreground line-clamp-2">
											{meso.description}
										</p>
									)}
									<p className="text-[10px] text-muted-foreground">
										{formatDate(meso.start_date)} →{" "}
										{formatDate(meso.end_date)}
									</p>
								</CardContent>
							</Card>
							);
						})}
					</div>
				)}
				{selectedMesocycleRange && mesocycleSelection.active && (
					<div className="text-xs text-volt bg-volt/10 border border-volt/20 rounded-sm px-3 py-2">
						{t("mesocycleSelectionActive")}{" "}
						{formatDate(selectedMesocycleRange.start_date)} -{" "}
						{formatDate(selectedMesocycleRange.end_date)}
					</div>
				)}

				{/* Monthly Calendar */}
				<Card className="bg-card border-border rounded-sm">
					<CardContent className="p-4">
						<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
							<div className="flex items-center gap-1">
								<Button
									variant="ghost"
									size="icon"
									className="rounded-sm h-8 w-8"
									onClick={prevMonth}
								>
									<ChevronLeft className="w-4 h-4" />
								</Button>
								<Button
									variant="ghost"
									size="icon"
									className="rounded-sm h-8 w-8"
									onClick={nextMonth}
								>
									<ChevronRight className="w-4 h-4" />
								</Button>
							</div>
							<p className="font-heading text-sm sm:text-base capitalize min-w-0 text-left sm:text-center">
								{monthRangeLabel}
							</p>
							{monthsSelectable && (
								<div className="flex items-center gap-2 w-full sm:w-auto">
									<span className="text-[10px] text-muted-foreground uppercase tracking-widest">
										{t("monthsVisible")}
									</span>
									<Select
										value={String(monthsToShow)}
										onValueChange={(value) => setMonthsToShow(Number(value))}
									>
										<SelectTrigger className="w-[96px] h-8 rounded-sm bg-background border-border text-xs ml-auto sm:ml-0">
											<SelectValue />
										</SelectTrigger>
										<SelectContent className="bg-card border-border rounded-sm">
											<SelectItem value="1" className="text-xs">
												1 {t("monthSingular")}
											</SelectItem>
											<SelectItem value="2" className="text-xs">
												2 {t("monthsPlural")}
											</SelectItem>
											<SelectItem value="3" className="text-xs">
												3 {t("monthsPlural")}
											</SelectItem>
										</SelectContent>
									</Select>
								</div>
							)}
						</div>
						<p className="text-[11px] text-muted-foreground mb-3">
							{t("coachCompetitionInsertHint")}
						</p>
						{loading ? (
							<div className="flex items-center justify-center py-10 text-muted-foreground">
								<Loader2 className="w-5 h-5 animate-spin mr-2" />
								{t("loading")}
							</div>
						) : (
							<>
								<div
									className={`grid gap-3 sm:gap-4 ${
										compact ? "grid-cols-1" : "grid-cols-1 md:grid-cols-2 xl:grid-cols-3"
									}`}
								>
									{visibleMonths.map((monthItem) => (
										<div
											key={`${monthItem.year}-${monthItem.month}`}
											className="border border-border rounded-sm p-2"
										>
											<p className="text-xs font-heading capitalize mb-2 text-center">
												{monthItem.label}
											</p>
											<div className="grid grid-cols-7 mb-1">
												{dayNames.map((d) => (
													<div
														key={`${monthItem.year}-${monthItem.month}-${d}`}
														className="text-center text-[10px] text-muted-foreground font-medium py-1"
													>
														{d}
													</div>
												))}
											</div>
											<div className="grid grid-cols-7">
												{renderCalendar(monthItem.year, monthItem.month)}
											</div>
										</div>
									))}
								</div>
							</>
						)}
					</CardContent>
				</Card>

				{/* Competition list below calendar */}
				{competitions.length > 0 && (
					<div>
						<p className="label-tactical mb-2">
							{t("athleteCompetitions")}
						</p>
						<div className="space-y-1.5">
							{competitions.map((event) => {
								const eventDate = new Date(event.date + "T12:00:00");
								const now = new Date();
								const isPast = eventDate < now;
								const daysUntil = Math.ceil(
									(eventDate - now) / (1000 * 60 * 60 * 24),
								);
								return (
									<div
										key={event.event_id}
										className={`flex items-center justify-between p-3 border border-border rounded-sm cursor-pointer transition-colors hover:border-volt/50 ${
											isPast ? "opacity-50" : "bg-card"
										}`}
										onClick={() => openEditCompetition(event)}
									>
										<div className="min-w-0 flex-1">
											<div className="flex items-center gap-2">
												<p className="font-heading text-sm truncate">
													{event.name}
												</p>
												{event.event_type && (
													<Badge className="bg-blaze/20 text-blaze border-0 text-[10px] rounded-sm">
														{event.event_type}
													</Badge>
												)}
												{(event.shared_athlete_count || 1) >= 2 && (
													<SharedAthletesBadge
														count={event.shared_athlete_count}
														onClick={() =>
															openCompetitionParticipants(event)
														}
													/>
												)}
											</div>
											<p className="text-[10px] text-muted-foreground">
												{eventDate.toLocaleDateString(
													language === "it" ? "it-IT" : "en-US",
													{
														day: "numeric",
														month: "short",
														year: "numeric",
													},
												)}
												{event.location && ` · ${event.location}`}
											</p>
										</div>
										{!isPast && (
											<div className="text-right shrink-0 ml-3">
												<p className="font-heading text-lg text-volt">
													{daysUntil}
												</p>
												<p className="text-[10px] text-muted-foreground">
													{t("days")}
												</p>
											</div>
										)}
										<div className="flex gap-1 shrink-0 ml-3">
											<button
												type="button"
												onClick={(clickEvent) => {
													clickEvent.stopPropagation();
													openEditCompetition(event);
												}}
												className="text-muted-foreground hover:text-volt p-0.5"
												aria-label={t("edit")}
											>
												<Edit2 className="w-3.5 h-3.5" />
											</button>
											<button
												type="button"
												onClick={(clickEvent) => {
													clickEvent.stopPropagation();
													deleteCompetitionEvent(event);
												}}
												className="text-muted-foreground hover:text-blaze p-0.5"
												aria-label={t("delete")}
											>
												<Trash2 className="w-3.5 h-3.5" />
											</button>
										</div>
									</div>
								);
							})}
						</div>
					</div>
				)}
			</div>
			<CompetitionParticipantsDialog
				open={Boolean(participantsEvent)}
				onOpenChange={(open) => {
					if (!open) setParticipantsEvent(null);
				}}
				event={participantsEvent}
				athletes={participants}
				loading={participantsLoading}
			/>

			{/* ── Mesocycle Dialog ── */}
			<Dialog
				open={isMesocycleDialogOpen}
				onOpenChange={setIsMesocycleDialogOpen}
			>
				<DialogContent className="bg-card border-border rounded-sm max-w-md">
					<DialogDescription className="hidden" />
					<DialogHeader>
						<DialogTitle className="font-heading">
							{editingMesocycle ? t("editMesocycle") : t("addMesocycle")}
						</DialogTitle>
					</DialogHeader>
					<div className="space-y-4">
						<div>
							<label className="label-tactical mb-1 block">{t("name")}</label>
							<Input
								className="bg-background border-border rounded-sm"
								value={mesocycleForm.name}
								onChange={(e) =>
									setMesocycleForm((f) => ({ ...f, name: e.target.value }))
								}
								placeholder={t("mesocycleNamePlaceholder")}
							/>
						</div>
						<div>
							<label className="label-tactical mb-1 block">
								{t("description")}
							</label>
							<Textarea
								className="bg-background border-border rounded-sm min-h-[80px]"
								value={mesocycleForm.description}
								onChange={(e) =>
									setMesocycleForm((f) => ({
										...f,
										description: e.target.value,
									}))
								}
								placeholder={t("mesocycleDescPlaceholder")}
							/>
						</div>
						<div className="grid grid-cols-2 gap-3">
							<div>
								<label className="label-tactical mb-1 block">
									{t("startDate")}
								</label>
								<Input
									type="date"
									className="bg-background border-border rounded-sm"
									value={mesocycleForm.start_date}
									onChange={(e) =>
										setMesocycleForm((f) => ({
											...f,
											start_date: e.target.value,
										}))
									}
								/>
							</div>
							<div>
								<label className="label-tactical mb-1 block">
									{t("endDate")}
								</label>
								<Input
									type="date"
									className="bg-background border-border rounded-sm"
									value={mesocycleForm.end_date}
									onChange={(e) =>
										setMesocycleForm((f) => ({
											...f,
											end_date: e.target.value,
										}))
									}
								/>
							</div>
						</div>
						<div>
							<label className="label-tactical mb-1 block">{t("color")}</label>
							<div className="flex flex-wrap gap-2">
								{MESOCYCLE_COLORS.map((c) => (
									<button
										key={c}
										onClick={() =>
											setMesocycleForm((f) => ({ ...f, color: c }))
										}
										className={`w-7 h-7 rounded-full border-2 transition-transform ${
											mesocycleForm.color === c
												? "border-foreground scale-110"
												: "border-transparent"
										}`}
										style={{ backgroundColor: c }}
									/>
								))}
							</div>
						</div>
						<div className="flex justify-end gap-2 pt-2">
							<Button
								variant="ghost"
								className="rounded-sm"
								onClick={() => setIsMesocycleDialogOpen(false)}
							>
								{t("cancel")}
							</Button>
							<Button
								className="bg-volt hover:bg-volt/90 rounded-sm"
								onClick={saveMesocycle}
							>
								<Save className="w-4 h-4 mr-1" />
								{t("save")}
							</Button>
						</div>
					</div>
				</DialogContent>
			</Dialog>

			<CompetitionEventDialog
				open={isCompetitionDialogOpen}
				onOpenChange={(open) => {
					setIsCompetitionDialogOpen(open);
					if (!open) setEditingCompetition(null);
				}}
				form={competitionForm}
				setForm={setCompetitionForm}
				onSave={saveCompetitionEvent}
				isEditing={Boolean(editingCompetition)}
			/>

		</>
	);
}
